import React, { useEffect, useState } from "react";
import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import Paper from "@mui/material/Paper";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import {ReactComponent as ExpandMoreIcon} from "../../icons/ExpandMore24.svg";
import dayjs from "dayjs";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import { getStudentPasses } from "../../services/PassService";
import { hasError, getError } from "../../services/ServiceHelper";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";
import TableRowNoResults from "../../components/TableRowNoResults";
import AccordionTitle from "../../components/AccordionTitle";

const PassHistory = () => {
  const STATUS_CREATED = 0;
  const STATUS_APPROVED = 50;
  const STATUS_DID_NOT_ARRIVE = 400;

  const [school] = useLocalStorage("Auth", "school", null);
  const [jwt] = useJwtStorage();

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const [_currentPasses, setCurrentPasses] = useState([]);
  const [_previousPasses, setPreviousPasses] = useState([]);
  const [selectedPass, setSelectedPass] = useState({});

  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    initStudentPasses();
  }, []);

  const initStudentPasses = () => {
    setShowProgress(true);

    getStudentPasses(school, jwt)
      .then((results) => {
        if (hasError(results) === true) {
          window.showSnack(
            "Unable to get Student Passes: " + getError(results)
          );
          return;
        }

        const allPasses = results.data;
        let cp = [];
        let pp = [];

        for (let i = 0; i < allPasses.length; i++) {
          const pass = allPasses[i];

          if (
            pass.status === STATUS_CREATED ||
            pass.status === STATUS_APPROVED ||
            pass.status === STATUS_DID_NOT_ARRIVE
          ) {
            cp.push(pass);
          } else {
            pp.push(pass);
          }
        }

        setCurrentPasses(cp);
        setPreviousPasses(pp);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const handlePassClick = (pass) => {
    setSelectedPass(pass);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const getPriorityText = (priority) => {
    if (priority === 500) {
      return "Student";
    }
    if (priority === 600 || priority === 700) {
      return "Teacher";
    }
    return "Admin";
  };

  const getDestinationText = (pass) => {
    if (pass.to_location_name != null) {
      return "(LOCATION) " + pass.to_location_name;
    }
    return (
      "(TEACHER) " +
      pass.to_teacher_last_name +
      ", " +
      pass.to_teacher_first_name
    );
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Pass History" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <Alert severity="info" key={alert.title}>
          <AlertTitle>Instructions</AlertTitle>
          Click pass for details.
        </Alert>

        <Accordion defaultExpanded sx={{ mt: 1 }} expanded={true}>
          <AccordionSummary
            className="accordionHeader"
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionTitle
              text={`Current Passes (${_currentPasses.length})`}
            />
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ mt: 0 }}>
              <Table sx={{ minWidth: 400 }} aria-label="score totals table">
                <TableBody>
                  {_currentPasses.map((pass) => (
                    <StripedTableRow
                      key={pass.eid}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                      }}
                      onClick={() => handlePassClick(pass)}
                    >
                      <StripedTableCell>
                        {dayjs(pass.date).format("MM/DD/YYYY")} - (
                        {pass.teacher_last_name})
                      </StripedTableCell>
                    </StripedTableRow>
                  ))}
                  <TableRowNoResults
                    data={_currentPasses}
                    text="-- NO CURRENT PASSES --"
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ mt: 1 }}>
          <AccordionSummary
            className="accordionHeader"
            expandIcon={<ExpandMoreIcon />}
          >
            <AccordionTitle
              text={`Previous Passes (${_previousPasses.length})`}
            />
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ mt: 0 }}>
              <Table sx={{ minWidth: 400 }} aria-label="score totals table">
                <TableBody>
                  {_previousPasses.map((pass) => (
                    <StripedTableRow
                      key={pass.eid}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        cursor: "pointer",
                      }}
                      onClick={() => handlePassClick(pass)}
                    >
                      <StripedTableCell>
                        {dayjs(pass.date).format("MM/DD/YYYY")} - (
                        {pass.teacher_last_name})
                      </StripedTableCell>
                    </StripedTableRow>
                  ))}
                  <TableRowNoResults
                    data={_previousPasses}
                    text="-- NO PREVIOUS PASSES --"
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      </Container>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth={"md"}
        fullWidth={true}
      >
        <DialogTitle>Pass Details</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table sx={{ width: "100%" }} aria-label="score totals table">
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography>Date Created</Typography>
                  </TableCell>
                  <TableCell>
                    {dayjs}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography>Pass Type</Typography>
                  </TableCell>
                  <TableCell>{selectedPass.type_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography>Status</Typography>
                  </TableCell>
                  <TableCell>{selectedPass.statusText}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography>Created By</Typography>
                  </TableCell>
                  <TableCell>
                    {selectedPass.created_by_last_name} (
                    {getPriorityText(selectedPass.created_by_priority)})
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography>Destination</Typography>
                  </TableCell>
                  <TableCell>{getDestinationText(selectedPass)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PassHistory;
